<template>
  <form class="variant-options-container" :class="brand.name" @submit.prevent>
    <div class="form-input-container">
      <div class="options-heading" :class="brand.name"> {{ categoryHeader }}</div>
      <div v-for="(option, index) in options" :key="index" class="variant-summary" :class="brand.name">
        <ButtonList
          v-if="optionDisplayComponent(option) === 'button'"
          required
          :tabindex="index"
          :brand="brand"
          :variant="variant"
          :optionLabel="option.label"
          :selectField="selectedOption(option.label)"
          :optionList="option.values"
          :inputClass="inputClass(index, option.label)"
          :errors="variantErrors(index, option)"
          v-on:click="selectVariantValue(option.label, $event.target.id)">
        </ButtonList>
        <DropDown class="variant-option-dropdown" :class="option.label"
          v-if="optionDisplayComponent(option) === 'dropdown'"
          required
          :tabindex="index"
          :selectOptions="option.values"
          :brand="brand"
          :label="option.label"
          :selectField="selectedOption(option.label)"
          :inputClass="inputClass(index, option.label)"
          :errors="variantErrors(index, option)"
          v-on:selected="selectVariantValue(option.label, $event.target.value)">
        </DropDown>
        <DropDown class="variant-option-dropdown"
          v-if="optionDisplayComponent(option) === 'yearDropdown'"
          required
          :tabindex="index"
          :selectOptions="getYearArray"
          :brand="brand"
          :label="option.label"
          :sorted=false
          :selectField="selectedOption(option.label)"
          :inputClass="inputClass(index, option.label)"
          :errors="variantErrors(index, option)"
          v-on:selected="selectVariantValue(option.label, $event.target.value)">
        </DropDown>
        <NumericInput class="variant-numeric-input"
          v-if="optionDisplayComponent(option) === 'numericInput'"
          required
          :tabindex="index"
          :brand="brand"
          :defaultNumber="selectedOption(option.label)"
          :label="option.label"
          :inputClass="inputClass(index, option.label)"
          :errors="variantErrors(index, option)"
          v-on:changeNumber="selectNumber(option.label, $event.target.value)"
          v-on:enter="handleSubmit">
        </NumericInput>
        <RadioButton
          v-if="optionDisplayComponent(option) === 'radioButton'"
          required
          :tabindex="index"
          :brand="brand"
          :label="option.label"
          :optionList="option.values"
          :defaultValue="selectedOption(option.label)"
          :inputClass="inputClass(index, option.label)"
          :errors="variantErrors(index, option)"
          v-on:input="selectVariantValue(option.label, $event.target.value)">
        </RadioButton>
      </div>
      <div class="alert" v-show="showCallDealershipMessage" :class="brand.name">
        <p>Currently, there are no online {{ brand[category].planTitle }} for your vehicle.</p>
        <p>Please contact <b>{{ dealer.name }}</b> to learn more about {{ brand[category].planTitle }} for your vehicle at
        <a v-bind:href="`tel:${bdcPhoneCompact}`" v-on:click="trackBdcPhone" class="alert-phone">{{ dealer.phone.bdc | phoneFormatter }}</a>.</p>
      </div>
    </div>
    <div class="button-container" :class="brand.name">
      <div class="error-message" v-if="v$.$error">Provide the required information to See Products.</div>
      <button class="ss-button show-product-button" id="show-product-button" :class="brand.name" v-on:click="handleSubmit" type="button" :disabled="showCallDealershipMessage">
        <span id="spinnerSubmit" class="spinner-border" role="status" aria-hidden="true" v-show="apiProcessing">
        </span>
        <i class="fa fa-shopping-cart"></i>
        See Products
      </button>
    </div>
    <div class="disclaimer" :class="brand.name" v-html="categoryDisclaimer"></div>
  </form>
</template>

<script>
import DropDown from '@/components/shared/DropDown'
import ButtonList from '@/components/shared/ButtonList'
import NumericInput from '@/components/shared/NumericInput'
import RadioButton from '@/components/shared/RadioButton'
import Tracking from '@/mixins/tracking-mixin'
import { mapState, mapGetters } from 'vuex'
import { EshopModel } from '@/models/eshop-model'
import { EntityParser } from '@/utils/entity-parser'
import { useBreadcrumbService } from '@/services/breadcrumb-service'
import { useEshopService } from '@/services/eshop-service'
import { required, helpers } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

export default {
  name: 'VariantSelectionByDefault',
  mixins: [Tracking],
  data: function () {
    const componentData = {}
    return this.dataReset(componentData)
  },
  props: {
    inIframe: Boolean,
    step: Number,
    category: String
  },
  async created () {
    await this.fetchData()
  },
  components: {
    DropDown,
    ButtonList,
    NumericInput,
    RadioButton
  },
  computed: {
    ...mapState({
      brand: state => state.brand,
      dealer: state => state.dealer,
      productVariants: state => state.productVariants,
      customer: state => state.customer,
      vehicle: state => state.vehicle
    }),
    ...mapGetters({
      getAvailableOptions: ['productVariants/getAvailableOptions'],
      getUserSelectedVariantsByCategory: ['productVariants/getUserSelectedVariantsByCategory']
    }),
    getYearArray () {
      return EntityParser.getArrayOfYears()
    },
    categoryHeader () {
      if (this.brand[this.category] && this.brand[this.category].variantSelectionHeading) {
        return this.brand[this.category].variantSelectionHeading
      } else {
        return ''
      }
    },
    categoryDisclaimer () {
      if (this.brand[this.category] && this.brand[this.category].disclaimer) {
        return this.brand[this.category].disclaimer
      } else {
        return ''
      }
    }
  },
  methods: {
    async fetchData () {
      this.loading = true
      await this.$store.dispatch('productVariants/getProductsSummaryByCategory', this.category)
      if (this.productVariants) {
        if (this.getAvailableOptions && this.getAvailableOptions.length > 0) {
          this.options = this.getAvailableOptions
          this.showCallDealershipMessage = false
        }
        // Retain seach criteria when the user navigates back
        const userSelection = this.getUserSelectedVariantsByCategory(this.category)
        if (userSelection && userSelection.length > 0) {
          // Default options to current selection/selected cart item if available
          this.variant = EshopModel.selectVariantsWithCart(this.options, userSelection, this.vehicle).variant
        } else {
          await this.$store.dispatch('customer/getDetails')
          // Default options to known vehicle is available
          this.variant = EshopModel.selectVariantsWithKnownVehicle(this.options, this.vehicle)
        }
      }

      this.loading = false
    },
    dataReset (src) {
      const { setSteps } = useBreadcrumbService()
      const { optionDisplayComponent } = useEshopService()
      src.optionDisplayComponent = optionDisplayComponent
      src.setSteps = setSteps
      src.loading = true
      src.apiProcessing = false
      src.showCallDealershipMessage = true
      src.options = []
      src.variant = [{ input: '', label: '', value: [] }]
      return src
    },
    inputClass (index, label) {
      if (this.v$.$errors[0] && this.v$.$errors[0].$message[index][0]) {
        return label + ' is-invalid'
      } else {
        return label
      }
    },
    variantErrors (index, option) {
      const variantError = []
      if (this.v$.$errors[0] && this.v$.$errors[0].$message[index][0]) {
        if (this.optionDisplayComponent(option) === 'radioButton') {
          variantError.push('Make a selection')
        } else if (this.optionDisplayComponent(option) === 'numericInput') {
          variantError.push('Enter required value')
        } else {
          variantError.push(this.v$.$errors[0].$message[index][0])
        }
      }
      return variantError
    },
    selectNumber (selectLabel, eventValue) {
      let numberChange = eventValue.replace(/\D+/g, '')
      numberChange = new Intl.NumberFormat().format(numberChange)
      this.selectVariantValue(selectLabel, numberChange)
    },
    selectVariantValue (selectLabel, eventValue) {
      // if they change a selection then hide error message
      this.showCallDealershipMessage = false
      const selected = this.variant.find(({ label }) => label.toLowerCase() === selectLabel.toLowerCase())
      if (selected) {
        this.variant.splice(this.variant.indexOf(selected), 1, { label: selectLabel.toLowerCase(), input: eventValue })
      } else {
        this.variant.push({ label: selectLabel.toLowerCase(), input: eventValue })
      }
    },
    selectedOption (selectLabel) {
      // if the user goes back to the page then reset to the last set of selected values
      let matchedOption
      // 1. match to the last set of selected variant
      if (this.variant && Object.keys(this.variant).length > 0) {
        const variantMatch = this.variant.find(({ label }) => label.toLowerCase() === selectLabel.toLowerCase())
        matchedOption = variantMatch && variantMatch.input ? variantMatch.input : ''
      }
      // 2. match to the customers known vehicle
      if (!matchedOption && (selectLabel.toLowerCase().includes('year') || selectLabel.toLowerCase().includes('model'))) {
        matchedOption = this.vehicle && Object.keys(this.vehicle).length > 0 && this.vehicle[selectLabel.toLowerCase()] ? this.vehicle[selectLabel.toLowerCase()] : ''
      }
      return matchedOption && matchedOption.toString()
    },
    async validateVariants () {
      const validatedModel = EshopModel.selectVariantsWithCart(this.options, this.variant, this.vehicle)
      this.variant = validatedModel.variant
      if (!validatedModel.valid) {
        this.showCallDealershipMessage = true
      } else {
        await this.$store.dispatch('productVariants/getProductsByVariants', { variantArray: this.variant, category: this.category })
        this.showCallDealershipMessage = !this.productVariants.hasProduct
      }
    },
    async handleSubmit () {
      this.v$.$touch() // trigger input validation
      if (!this.v$.$error) {
        await this.validateVariants() // match selection to valid product
        // if we matched a product then proceed
        if (!this.showCallDealershipMessage) {
          this.apiProcessing = true
          // this is needed for us to reload known vehicle after call to Stripe
          this.$store.commit('vehicle/setVehicle', { result: this.vehicle })
          this.$store.commit('productVariants/setSelectedVariantsByCategory', { category: this.category, variants: this.variant })
          this.setSteps({ currentStep: 2, category: this.category })
          this.$emit('navigate')
          this.apiProcessing = !this.inIframe
        } else {
          this.$store.dispatch('session/logInteraction', { interaction_type: 'eshop', action: 'call_dealership', metadata: { vehicle: this.vehicle, variant: this.variant } })
        }
      }
    }
  },
  setup () {
    return {
      v$: useVuelidate()
    }
  },
  validations () {
    return {
      variant: {
        $each: helpers.forEach({
          input: {
            required: helpers.withMessage('Make a selection', required)
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.options-heading {
  font-weight: bold;
  text-align: center;
}
.options-heading.mini {
  text-align: left;
}
.variant-options-container {
  display: flex;
  flex-direction: column;
  text-align: left;
  background-color: #ffffff;
  padding: 5px;
  width: 100%; /* sets form size */
}
.variant-options-container.mini {
  align-self: center;
  align-content: flex-start;
}
.variant-summary {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.variant-numeric-input,
.variant-option-dropdown {
  width: 100%;
  margin: 10px 0px;
}
.form-input-container {
  display: flex;
  flex-direction: column;
  row-gap: 5px;
}
.button-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  row-gap: 20px;
  text-align: center;
  align-items: center;
  margin: 30px 0px;
}
.button-container.bmw,
.button-container.mini {
  justify-content: left;
  text-align: left;
  align-items: flex-start;
}
.mobile-button {
  font-size: 0.75rem;
  display: flex;
  cursor: pointer;
  margin-top: 20px;
}

.show-product-button.volvo {
  width: 50%;
}
.disclaimer {
  margin-top: 20px;
  font-size: 0.5rem;
  text-align: center;
}
.alert {
  padding-top: 20px;
}
.alert-phone {
  color: black;
  font-weight: bold;
}
.error-message {
  color: var(--error-color);
}
/* styles for different screen widths follow */
@media only screen and (min-width: 576px) {
  .alert {
    flex-direction: column;
  }
  .alert,
  .variant-summary {
    display: flex;
    justify-self: center;
    align-self: center;
    width: 80%;
  }
  .alert.mini,
  .disclaimer.mini,
  .button-container.mini,
  .variant-summary.mini {
    justify-self: left;
    align-self: flex-start;
    text-align: left;
  }
}
@media only screen and (min-width: 768px) {
  .alert,
  .button-container,
  .variant-summary {
    display: flex;
    justify-self: center;
    align-self: center;
    width: 50%;
  }
}
@media only screen and (min-width: 992px) {
  .variant-options-container.mini {
    width: 80%;
  }
}
@media only screen and (min-width: 1200px) {
}
</style>
